import { useContext } from 'react'

import { useGetUserPermissionQuery } from 'apis/AVPPermission/getPermissionAllAction'
import { AVP_SERVICE_ADMIN } from 'constants/avp_permissions'
import AuthContext from 'contexts/AuthContext'

const ADMIN_LIST_ACTION = 'Admin:ListProject'
const ADMIN_PROJECT_READ_ACTION = 'Admin:GetProject'
const ADMIN_PROJECT_WRITE_ACTION = 'Admin:EditProject'
const ADMIN_SUBPROJECT_CREATE_ACTION = 'Admin:CreateSubproject'
const ADMIN_SUBPROJECT_LIST_ACTION = 'Admin:ListSubproject'
const ADMIN_SUBPROJECT_READ_ACTION = 'Admin:GetSubproject'
const ADMIN_SUBPROJECT_EDIT_ACTION = 'Admin:EditSubproject'
const ADMIN_PERMISSION_LIST_ACTION = 'Admin:ListPermissions'
const ADMIN_PERMISSION_READ_ACTION = 'Admin:GetPermission'
const ADMIN_PERMISSION_CREATE_UPDATE_ACTION = 'Admin:CreateUpdatePermissions'
const ADMIN_PERMISSION_DELETE_ACTION = 'Admin:DeletePermission'
const ADMIN_PERMISSION_EXPORT_ACTION = 'Admin:ExportPermissions'
const ADMIN_ROLE_LIST_ACTION = 'Admin:ListRole'
const ADMIN_ROLE_READ_ACTION = 'Admin:GetRole'
const ADMIN_ROLE_CREATE_UPDATE_ACTION = 'Admin:CreateUpdateRole'
const ADMIN_ROLE_DELETE_ACTION = 'Admin:DeleteRole'
const ADMIN_ROLE_CLONE_ACTION = 'Admin:CloneRole'

export const useAdminPermission = (projectId?: string | number) => {
  const { isStaff } = useContext(AuthContext)

  const { data, isLoading } = useGetUserPermissionQuery({
    projectId: String(projectId || ''),
    serviceName: AVP_SERVICE_ADMIN,
    config: {
      enabled: !isStaff && !!projectId,
      staleTime: 1000,
    },
  })

  const listActions = data?.project?.list ?? []
  const readActions = data?.project?.read ?? []
  const writeActions = data?.project?.write ?? []

  const canListProjects = isStaff || listActions.includes(ADMIN_LIST_ACTION)
  const canWriteProject = isStaff || writeActions.includes(ADMIN_PROJECT_WRITE_ACTION)
  const canCreateSubprojects =
    isStaff || writeActions.includes(ADMIN_SUBPROJECT_CREATE_ACTION)
  const canEditSubprojects =
    isStaff || writeActions.includes(ADMIN_SUBPROJECT_EDIT_ACTION)
  const canCreateUpdatePermissions =
    isStaff || writeActions.includes(ADMIN_PERMISSION_CREATE_UPDATE_ACTION)
  const canDeletePermissions =
    isStaff || writeActions.includes(ADMIN_PERMISSION_DELETE_ACTION)
  const canListPermissions =
    isStaff || listActions.includes(ADMIN_PERMISSION_LIST_ACTION)
  const canReadPermissions =
    isStaff || readActions.includes(ADMIN_PERMISSION_READ_ACTION)
  const canExportPermissions =
    isStaff || listActions.includes(ADMIN_PERMISSION_EXPORT_ACTION)
  const canManagePermissions =
    canListPermissions ||
    canReadPermissions ||
    canCreateUpdatePermissions ||
    canDeletePermissions
  const canCreateUpdateRoles =
    isStaff || writeActions.includes(ADMIN_ROLE_CREATE_UPDATE_ACTION)
  const canCloneRoles =
    isStaff || writeActions.includes(ADMIN_ROLE_CLONE_ACTION)
  const canDeleteRoles =
    isStaff || writeActions.includes(ADMIN_ROLE_DELETE_ACTION)
  const canListRoles =
    isStaff || listActions.includes(ADMIN_ROLE_LIST_ACTION)
  const canReadRoles =
    isStaff || readActions.includes(ADMIN_ROLE_READ_ACTION)
  const canManageRoles =
    canListRoles ||
    canReadRoles ||
    canCreateUpdateRoles ||
    canDeleteRoles ||
    canCloneRoles
  const canReadProject =
    isStaff || readActions.includes(ADMIN_PROJECT_READ_ACTION)

  const canListSubprojects =
    isStaff || listActions.includes(ADMIN_SUBPROJECT_LIST_ACTION)
  const canWriteSubprojects = canEditSubprojects
  const canReadSubprojects =
    isStaff || readActions.includes(ADMIN_SUBPROJECT_READ_ACTION)

  return {
    isLoading: !isStaff && !!projectId ? isLoading : false,
    canAccessAdmin:
      isStaff ||
      canListProjects ||
      canReadProject ||
      canWriteProject ||
      canCreateSubprojects ||
      canEditSubprojects ||
      canManagePermissions ||
      canManageRoles ||
      canReadSubprojects ||
      canWriteSubprojects,
    canListProjects,
    canReadProject,
    canWriteProject,
    canCreateSubprojects,
    canEditSubprojects,
    canManagePermissions,
    canListPermissions,
    canReadPermissions,
    canCreateUpdatePermissions,
    canDeletePermissions,
    canExportPermissions,
    canManageRoles,
    canListRoles,
    canReadRoles,
    canCreateUpdateRoles,
    canCloneRoles,
    canDeleteRoles,
    canListSubprojects,
    canReadSubprojects,
    canWriteSubprojects,
    permissions: data,
  }
}
